import React, { memo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import cn, { Argument as ClassName } from 'classnames'
import { useMediaPredicate } from 'react-media-hook'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTablePageEvent } from 'primereact/datatable'
import { Card } from 'primereact/card'

import useText from '../../../common/hooks/useText'
import { Table } from '../../../common/uikit/Table'
import { Input } from '../../../common/uikit/Input'
import { Dropdown } from '../../../common/uikit/Dropdown'
import {
    CompanyPreview,
    PagedResultOfLoadBalancerGroup,
    LoadBalancerGroupGetAllPagedApiArg,
    LoadBalancerGroup,
    Company,
} from '../../../common/redux/api/web-api'
import { isAdminDomain } from '../../../lib/currentDomain'
import { RootState } from '../../redux/store'

import { CreateLoadBalancerGroupDialog } from './dialogs/create-load-balancer-group-dialog'

export type LoadbalancerTableListWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    loadBalancerGroups: PagedResultOfLoadBalancerGroup | null | undefined
    companies?: Company[] | CompanyPreview[] | null | undefined
    removeLBGroup: (value?: number) => void
    filterSubmit: (values: Partial<LoadBalancerGroupGetAllPagedApiArg>) => void
    filter: LoadBalancerGroupGetAllPagedApiArg
    groupsLoading?: boolean
    setFilter: (filter: Partial<LoadBalancerGroupGetAllPagedApiArg>) => void
}

const emptyLoadBalancerGroup: LoadBalancerGroup = {}

export const LoadbalancerTableListWidget = memo(function LoadbalancerTableListWidget({
    className,
    style,
    loadBalancerGroups,
    companies,
    removeLBGroup,
    filterSubmit,
    filter,
    setFilter,
    groupsLoading,
}: LoadbalancerTableListWidgetProps): React.ReactElement {
    const text = useText()
    const [openAddModal, setOpenAddModal] = useState<boolean>(false)
    const [lazyState, setLazyState] = useState({
        first: (filter?.page ?? 0) * (filter?.noOfItems ?? 15),
        rows: filter?.noOfItems ?? 15,
        page: filter?.page ?? 0,
    })

    const onPage = (event: DataTablePageEvent) => {
        const newPage = event.first / event.rows
        setLazyState({
            first: event.first,
            rows: event.rows,
            page: newPage,
        })
        setFilter({ ...filter, page: newPage, noOfItems: event.rows })
    }

    const { register, handleSubmit, control, reset } = useForm<LoadBalancerGroupGetAllPagedApiArg>({
        defaultValues: filter,
    })

    const isMobileScreen = useMediaPredicate('(max-width: 600px)')

    const allowAccess = useSelector((state: RootState) => state.user.isSuperAdmin || state.user.isPartner)

    const companyOptions =
        companies?.map((company) => ({
            value: company?.name,
            label: company?.name || '',
        })) || []

    const columns = []

    if (!isMobileScreen) {
        columns.push(
            <Column
                key="id"
                field="id"
                header={text('lblId')}
                body={(group: LoadBalancerGroup) => <Link to={`/loadbalancer/${group?.id}`}>{group?.id}</Link>}
            />
        )
    }

    columns.push(
        <Column
            key="name"
            field="name"
            header={text('lblName')}
            style={{ width: '200px' }}
            body={(group: LoadBalancerGroup) => {
                return isMobileScreen ? <Link to={`/loadbalancer/${group?.id}`}>{group?.name}</Link> : group?.name
            }}
        />
    )

    if (!isAdminDomain) {
        columns.push(<Column key="companyName" field="companyName" header={text('lblOrganisation')} />)
    }

    columns.push(
        <Column
            key="actions"
            body={(group: LoadBalancerGroup) => (
                <div className="flex justify-content-end">
                    <Button
                        icon="pi pi-trash"
                        severity="danger"
                        size="small"
                        outlined
                        rounded
                        disabled={group?.active || !allowAccess}
                        data-testid={group?.active ? `disabled-remove-${group?.id}` : `enabled-remove-${group?.id}`}
                        onClick={() => removeLBGroup(group?.id)}
                        tooltipOptions={{ showOnDisabled: true }}
                        tooltip={(() => {
                            if (group?.active) {
                                return text('msgLbActive')
                            } else if (!allowAccess) {
                                return text('msgDeniedForNonAdministratorsLBgroup')
                            } else {
                                return undefined
                            }
                        })()}
                    />
                </div>
            )}
        />
    )

    return (
        <Card className={cn(className)} style={style}>
            <CreateLoadBalancerGroupDialog
                visible={openAddModal}
                onHide={() => setOpenAddModal(false)}
                organisations={companies ?? []}
                group={emptyLoadBalancerGroup}
            />

            <form
                name="form"
                role="form"
                noValidate
                onSubmit={handleSubmit((values) => {
                    filterSubmit(values)
                })}
                className="mb-4"
            >
                <div className="flex gap-4 flex-wrap">
                    {!isMobileScreen && (
                        <div className="flex-1">
                            <Input
                                label={text('lblId')}
                                {...register('id')}
                                data-testid="input-lb-id"
                                id="id"
                                className="search-input"
                            />
                        </div>
                    )}
                    <div className="flex-1">
                        <Input
                            label={text('lblName')}
                            {...register('name')}
                            data-testid="input-lb-name"
                            id="lbName"
                            className="search-input"
                        />
                    </div>
                    {!isAdminDomain && (
                        <div className="flex-1">
                            <Controller
                                name="companyName"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Dropdown
                                        id="organisation"
                                        label={text('lblOrganisation')}
                                        options={companyOptions}
                                        onChange={(e) => {
                                            onChange(e.value ?? null)
                                        }}
                                        showClear
                                        data-testid="company-input"
                                        value={value || null}
                                    />
                                )}
                            />
                        </div>
                    )}
                </div>
                <div className="flex gap-4 mt-5 justify-content-between align-items-center">
                    {!isAdminDomain && (
                        <Button
                            label={text('btnCreate')}
                            icon="pi pi-plus"
                            onClick={() => setOpenAddModal(true)}
                            type="button"
                        />
                    )}
                    <div className="flex align-self-end justify-content-end gap-4">
                        <Button
                            label={text('lblSearch')}
                            data-testid="btn-search"
                            loading={groupsLoading}
                            type="submit"
                        />
                        <Button
                            label={text('btnClear')}
                            data-testid="btn-clear"
                            onClick={() => reset()}
                            type="submit"
                        />
                    </div>
                </div>
            </form>

            <Table
                value={loadBalancerGroups?.items || []}
                loading={groupsLoading}
                lazy
                paginator
                first={lazyState.first}
                rows={lazyState.rows}
                totalRecords={loadBalancerGroups?.totalCount ?? 0}
                onPage={onPage}
            >
                {columns}
            </Table>
        </Card>
    )
})
